import React, { useState } from 'react';
import { UserAccount } from './auth-types';
import { faceIdService } from './face-id-service';
import { multiAccountVault } from './multi-account-vault';

interface ProfileFaceIdModalProps {
  user: UserAccount;
  onClose: () => void;
}

export const ProfileFaceIdModal: React.FC<ProfileFaceIdModalProps> = ({ user, onClose }) => {
  const [selectedAvatarSeed, setSelectedAvatarSeed] = useState(user.displayName.toLowerCase().replace(/\s+/g, '_'));
  const [isScanning, setIsScanning] = useState(false);
  const [enrolled, setEnrolled] = useState(!!user.faceIdProfile?.enrolled);
  const [livenessScore, setLivenessScore] = useState(user.faceIdProfile?.livenessConfidenceScore || 0);

  const currentAvatarUrl = `https://api.dicebear.com/7.x/bottts/svg?seed=${selectedAvatarSeed}`;

  const handleEnrollFaceId = () => {
    setIsScanning(true);
    setTimeout(() => {
      const profile = faceIdService.enrollFaceProfile(user, currentAvatarUrl, 0.99);
      multiAccountVault.saveUser(user);
      setEnrolled(true);
      setLivenessScore(profile.livenessConfidenceScore);
      setIsScanning(false);
    }, 1200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-subtle bg-bg-surface p-6 shadow-2xl space-y-6">
        <div className="flex items-center justify-between border-b border-border-subtle pb-3">
          <div className="flex items-center space-x-2">
            <span className="text-xl">👤</span>
            <h2 className="text-base font-bold text-text-primary">Profile & Face ID Enrollment</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary text-sm font-mono"
          >
            ✕
          </button>
        </div>

        {/* Avatar Setup Section */}
        <div className="flex flex-col items-center space-y-3">
          <div className="relative h-24 w-24 rounded-full border-2 border-accent-primary overflow-hidden bg-bg-primary shadow-inner">
            <img src={currentAvatarUrl} alt="Avatar" className="h-full w-full object-cover" />
            {enrolled && (
              <span className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-[10px] text-white font-bold" title="Face ID Enrolled">
                ✓
              </span>
            )}
          </div>

          <div className="w-full space-y-1">
            <label className="text-xs font-mono text-text-muted">Avatar Seed / Style</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={selectedAvatarSeed}
                onChange={(e) => setSelectedAvatarSeed(e.target.value)}
                className="flex-1 rounded-lg border border-border-subtle bg-bg-primary px-3 py-1.5 text-xs text-text-primary font-mono focus:border-accent-primary focus:outline-none"
                placeholder="Enter seed name..."
              />
              <button
                type="button"
                onClick={() => setSelectedAvatarSeed(Math.random().toString(36).substring(2, 8))}
                className="rounded-lg border border-border-subtle bg-bg-primary px-3 py-1.5 text-xs font-mono text-text-primary hover:border-accent-primary"
              >
                🎲 Random
              </button>
            </div>
          </div>
        </div>

        {/* Face ID Biometric Enroller */}
        <div className="rounded-xl border border-border-subtle bg-bg-primary p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-lg">📸</span>
              <div>
                <h3 className="text-xs font-bold text-text-primary">Optical Face ID Biometrics</h3>
                <p className="text-[11px] text-text-muted">For passwordless login & physical door access</p>
              </div>
            </div>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-mono font-bold ${
                enrolled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'
              }`}
            >
              {enrolled ? 'ACTIVE' : 'NOT ENROLLED'}
            </span>
          </div>

          {enrolled && (
            <div className="flex items-center justify-between text-[11px] font-mono text-text-muted border-t border-border-subtle/50 pt-2">
              <span>Liveness Score:</span>
              <span className="text-emerald-400 font-bold">{(livenessScore * 100).toFixed(0)}% (Verified)</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleEnrollFaceId}
            disabled={isScanning}
            className={`w-full rounded-lg py-2 text-xs font-mono font-bold transition flex items-center justify-center space-x-2 ${
              isScanning
                ? 'bg-accent-primary/50 text-bg-primary cursor-wait'
                : 'bg-accent-primary text-bg-primary hover:opacity-90'
            }`}
          >
            {isScanning ? (
              <>
                <span className="h-3 w-3 rounded-full border-2 border-bg-primary border-t-transparent animate-spin" />
                <span>Capturing Facial Embedding...</span>
              </>
            ) : (
              <span>{enrolled ? 'Re-Enroll Face ID' : 'Enroll Face ID with Current Avatar'}</span>
            )}
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle bg-bg-surface px-4 py-2 text-xs font-mono text-text-primary hover:border-accent-primary"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
